import React, { useMemo } from 'react';

import styled from 'styled-components';

import { getFolderContacts } from '@libs/NwFolders';

import { getContactName, getSimpleContacts, getContact } from '@hooks/API/Contact/UseContactsList';
import {
    useAddContactsToFolder,
    useFolders,
    useOrderFolderContacts,
    useRemoveContactsFromFolder,
} from '@hooks/API/UseFolders';

import ContactsQuickAdd from '@components/ContactsQuickAdd/ContactsQuickAdd';
import { useContactsSelector } from '@components/ContactsSelector/Common/ContactsSelectorContext';
import CenteredBlock from '@components/Gui/CenteredBlock';
import EmptyState from '@components/Gui/EmptyState';
import FlexContainer from '@components/Gui/FlexContainer';
import { NwButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDivider from '@components/Gui/NwDivider';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const Wrapper = styled.div`
    padding: 1rem;
`;

const NameContainer = styled.div`
    display: flex;
    align-items: center;
    ${(props) => (props.isInStorage ? props.theme.RedLineThrough(1) : 'text-decoration: none;')}
    opacity: ${(props) => props.isNotAccessible && 0.5};
`;

const Label = styled.div`
    margin-left: 2rem;
    text-transform: uppercase;
`;

const SortableList = styled.div`
    opacity: ${(props) => (props.isLoading ? '.25' : '1')};
    position: relative;
    cursor: pointer;
`;

const SortableListItem = styled.div`
    background: ${(props) => (props.isDragging ? StyleVariables.PrimaryColor : '#fff')};
    color: ${(props) => (props.isDragging ? '#fff' : StyleVariables.TextColor)};
    margin-bottom: 4px;
    padding: 0.5rem;
    box-shadow: ${(props) => (props.isDragging ? '0 1px 6px rgba(57,73,76,.35)' : null)};
    border-bottom: 0.5px solid ${(props) => props.theme.LighterBorderColor};
    display: flex;
    align-items: center;
    width: 100%;

    .sortable-list-item-icon {
        margin: 0 2rem 0 0;
    }

    .sortable-list-item-text {
        h6,
        p {
            padding: 0;
            margin: 0;
        }
    }
`;

const FolderContactsList = () => {
    const { selection, context } = useContactsSelector();
    const { activeSet } = selection;
    const { itemType } = context;

    const { data: folders } = useFolders();
    const { mutateAsync: addContactsToFolder, isLoading: isAdding } = useAddContactsToFolder();
    const { mutateAsync: removeContactsFromFolder, isLoading: isRemoving } = useRemoveContactsFromFolder();
    const { mutateAsync: orderFolderContacts, isLoading: isOrdering } = useOrderFolderContacts();

    const folder = folders && folders.find((folder) => folder.ID === Number(activeSet));

    const folderContacts = getFolderContacts(folder);

    const onItemDelete = async (itemID) => {
        try {
            await removeContactsFromFolder({ folderID: folder.ID, contactIDs: [itemID] });
        } catch (error) {
            console.error('Remove Folder Contact Error => ', error);
        }
    };

    const items = useMemo(() => {
        return folderContacts.map((itemID) => {
            return {
                Name: getContactName(itemID),
                ID: itemID,
            };
        });
    }, [folderContacts]);

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const onDragEnd = async (result) => {
        // Controlla se esiste una destinazione valida
        if (!result.destination) {
            return;
        }

        // Controlla se l'indice è effettivamente cambiato
        if (result.source.index !== result.destination.index) {
            const reorderedContacts = reorder([...folderContacts], result.source.index, result.destination.index);
            try {
                await orderFolderContacts({ folderID: folder.ID, contactIDs: reorderedContacts });
            } catch (error) {
                console.error('Order Folder Contacts Error => ', error);
            }
        }
    };

    const handleAddContact = async (itemID) => {
        try {
            await addContactsToFolder({ folderID: folder.ID, contactIDs: [itemID] });
        } catch (error) {
            console.error('Add Folder Contact Error => ', error);
        }
    };

    const simpleContacts = getSimpleContacts();

    //testo delle due sezioni in base al tipo di folder: "model"/"customer"
    const itemLabel = itemType === 'model' ? 'model' : 'customer';

    if (!folder) {
        return null;
    }

    return (
        <Wrapper>
            {/* nome, descrizione e privacy sono nella riga identita' sopra, dentro FolderManageDrawer */}
            <NwDivider>Add a {itemLabel}</NwDivider>
            <ContactsQuickAdd
                onSelect={handleAddContact}
                sources={itemType === 'model' ? ['models'] : ['clients', 'agencies', 'services']}
                added={folderContacts}
            />
            <NwDivider>Sort {itemLabel}s</NwDivider>
            {items.length > 0 ? (
                <NwCard>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable">
                            {(provided) => (
                                <SortableList
                                    isLoading={isAdding || isRemoving || isOrdering}
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {items.map((item, index) => {
                                        const simpleContact = simpleContacts.find((contact) => contact.ID === item.ID);
                                        const contact = getContact(item.ID);
                                        const isInStorage =
                                            !contact && simpleContact && simpleContact.SB ? true : false;
                                        const isNotAccessible =
                                            !contact && simpleContact && !simpleContact.SB ? true : false;
                                        return (
                                            <Draggable key={item.ID} draggableId={String(item.ID)} index={index}>
                                                {(provided, snapshot) => (
                                                    <SortableListItem
                                                        isDragging={snapshot.isDragging}
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <FlexContainer
                                                            fullWidth
                                                            justifyContent="space-between"
                                                            className="sortable-list-item-text"
                                                        >
                                                            <FlexContainer>
                                                                <NwIcon
                                                                    icon={byPrefixAndName.fasl['arrow-down-arrow-up']}
                                                                />
                                                                <NameContainer
                                                                    isInStorage={isInStorage}
                                                                    isNotAccessible={isNotAccessible}
                                                                >
                                                                    <Label>{item.Name}</Label>
                                                                </NameContainer>
                                                            </FlexContainer>
                                                            {isInStorage && <div>model in storage</div>}
                                                            {isNotAccessible && <div>not accessible</div>}
                                                            <NwButton
                                                                style={{ marginRight: '1rem' }}
                                                                iconOnly
                                                                ghost
                                                                icon={byPrefixAndName.fasl['times']}
                                                                size="small"
                                                                onClick={() => onItemDelete(item.ID)}
                                                            />
                                                        </FlexContainer>
                                                    </SortableListItem>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </SortableList>
                            )}
                        </Droppable>
                    </DragDropContext>
                </NwCard>
            ) : (
                <CenteredBlock>
                    <EmptyState message="no contacts fitting your search" />
                </CenteredBlock>
            )}
        </Wrapper>
    );
};

export default FolderContactsList;
